import { useTranslation } from "react-i18next";

import { CampAvatar } from "@/components/camp/CampAvatar";
import type { AnimalId } from "@/components/camp/campData";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLocale } from "@/hooks/useLocale";

interface HomeProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  animal: AnimalId;
  email: string | null;
  /** When the account was created, as the session reports it. */
  since: string | null;
}

/**
 * The account behind the character: the address it signs in with and how long
 * it has existed. The character itself is edited on its own page, which is why
 * this holds nothing to change.
 */
export function HomeProfileDialog({
  open,
  onOpenChange,
  name,
  animal,
  email,
  since,
}: HomeProfileDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const date = since
    ? new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(
        new Date(since),
      )
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="account-profile" className="sm:max-w-sm">
        <DialogHeader className="text-left">
          <DialogTitle>{t("settings.profile")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("account.signedInAs")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <CampAvatar beast={animal} size={48} />
          <p className="min-w-0 truncate text-base font-semibold">{name}</p>
        </div>
        <dl className="space-y-3 text-sm">
          <div>
            <dt className="text-muted-foreground">{t("account.signedInAs")}</dt>
            <dd
              data-testid="account-profile-email"
              className="truncate font-medium"
            >
              {email}
            </dd>
          </div>
          {date && (
            <div>
              <dt className="text-muted-foreground">
                {t("account.memberSince")}
              </dt>
              <dd data-testid="account-profile-since" className="font-medium">
                {date}
              </dd>
            </div>
          )}
        </dl>
      </DialogContent>
    </Dialog>
  );
}
